import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field, Stat, StatusBadge } from "@/components/UI";
import { Plus, Trash, Coins, Trophy, ChartLine, PencilSimple, Package as PackageIcon, Lock, MagnifyingGlass, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GroupedTable, PerformerField } from "@/components/VetTable";
import { today, CATEGORIES } from "./helpers";
import { TreatmentMonthlyCards } from "./TreatmentMonthlyCards";

const TR_MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const monthLabel = (ym) => { if (!ym || ym.length < 7) return ym || "—"; const [y, m] = ym.split("-"); return `${TR_MONTHS_FULL[Number(m) - 1] || m} ${y}`; };
const prevMonthKey = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };
const TREAT_TABS = [
  { key: "tedavi görüyor", label: "Aktif", countKey: "tedavi görüyor" },
  { key: "iyileşti", label: "Tamamlanan", countKey: "iyileşti" },
  { key: "vefat", label: "Vefat", countKey: "vefat" },
  { key: "iptal", label: "İptal", countKey: "iptal" },
  { key: "all", label: "Tümü", countKey: "all" },
  { key: "archive", label: "Arşiv", countKey: null },
];
const OUTCOME_TONE = { "tedavi görüyor": "text-[#8B6914]", "iyileşti": "text-[#2D4A36]", "vefat": "text-[#C45B45]", "iptal": "text-[#8a938c]" };

// Birim dönüşümü (backend ile aynı): hacim litre/ml, kütle kg/gr
const VOL = { litre: 1, lt: 1, l: 1, ml: 0.001, cc: 0.001 };
const MASS = { kg: 1, ton: 1000, gram: 0.001, gr: 0.001, g: 0.001 };
const DOSE_UNITS_FOR = (u) => {
  const s = (u || "").toLowerCase();
  if (VOL[s] !== undefined) return ["ml", "litre"];
  if (MASS[s] !== undefined) return ["gram", "kg"];
  return [s || "adet"];
};
const convertAmt = (amount, from, to) => {
  const f = (from || "").toLowerCase(), t = (to || "").toLowerCase();
  if (!f || f === t) return Number(amount);
  if (VOL[f] !== undefined && VOL[t] !== undefined) return Number(amount) * VOL[f] / VOL[t];
  if (MASS[f] !== undefined && MASS[t] !== undefined) return Number(amount) * MASS[f] / MASS[t];
  return null;
};

// Yüksek doz denetimi: girilen dozu ml (hacim) veya gr (kütle) cinsine çevirir.
// adet/doz gibi birimlerde null döner (uyarı yok). 1 L = 1000 ml.
const HIGH_DOSE = 50;
let _tuidc = 0;
const tuid = () => `tp${Date.now().toString(36)}${(_tuidc++).toString(36)}`;
const doseInBase = (amount, unit) => {
  const s = (unit || "").toLowerCase();
  if (VOL[s] !== undefined) return { value: Number(amount || 0) * VOL[s] * 1000, base: "ml" };
  if (MASS[s] !== undefined) return { value: Number(amount || 0) * MASS[s] * 1000, base: "gr" };
  return null;
};


export function Treatments() {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [items, setItems] = useState([]);
  const [meds, setMeds] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [expandedTreatment, setExpandedTreatment] = useState(null);
  const [mode, setMode] = useState("single"); // single | package
  const [pkgItems, setPkgItems] = useState([]); // [{medicine_id, amount, amount_unit}]
  const [selectedProtocolId, setSelectedProtocolId] = useState("");
  const [form, setForm] = useState({ animal_type: "inek", animal_count: 1, date: today(), diagnosis: "", medicine_id: "", medicine_amount: "", medicine_amount_unit: "", dose: "", treatment_duration_days: "", vet_name: "", outcome: "tedavi görüyor", notes: "" });

  // --- Durum sekmeleri (filtre) / arama / arşiv / aylık kapanış ---
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [tab, setTab] = useState("tedavi görüyor");
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({});
  const [closures, setClosures] = useState([]);
  const [archiveOpen, setArchiveOpen] = useState(null);
  const [archiveItems, setArchiveItems] = useState([]);
  const [closeMonth, setCloseMonth] = useState(prevMonthKey());

  // Aylık kartlar için TÜM açık (kilitsiz) tedavileri çek; sekme/arama istemci tarafında filtreler.
  const loadTreatments = async () => {
    let all = [], skipN = 0, c = {}, totalN = 0;
    try {
      do {
        const r = await api.get(`/vet/treatments?status=all&locked=false&limit=500&skip=${skipN}`);
        all = all.concat(r.data.items || []);
        totalN = r.data.total || 0; c = r.data.counts || c; skipN += 500;
      } while (all.length < totalN && skipN < 10000);
      setItems(all); setCounts(c);
    } catch { /* geçici kesinti — mevcut veriyi koru */ }
  };
  const loadClosures = () => api.get("/vet/treatments/closures").then(r => setClosures(r.data || [])).catch(() => {});
  const load = () => { loadTreatments(); if (tab === "archive") loadClosures(); };
  const loadMeds = () => api.get(`/medicines?category=${encodeURIComponent("İlaç")}`).then(r => setMeds(r.data)).catch(() => {});
  const loadProtocols = () => api.get("/medicines/protocols").then(r => setProtocols(r.data)).catch(() => {});

  useEffect(() => { loadMeds(); loadProtocols(); loadTreatments(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "archive") loadClosures(); }, [tab]);

  const openArchiveMonth = (month) => {
    if (archiveOpen === month) { setArchiveOpen(null); return; }
    setArchiveOpen(month); setArchiveItems([]);
    api.get(`/vet/treatments?month=${month}&locked=true&limit=500`).then(r => setArchiveItems(r.data.items || [])).catch(() => {});
  };
  const doCloseMonth = async () => {
    if (!closeMonth) return toast.error("Kapatılacak ayı seçin");
    if (!window.confirm(`${monthLabel(closeMonth)} ayını kapatmak istediğinize emin misiniz?\n\n• Tamamlanan / Vefat / İptal kayıtlar KİLİTLENİR (düzenlenemez).\n• Hâlâ "tedavi görüyor" olanlar bir sonraki aya DEVREDER.`)) return;
    try {
      const r = await api.post("/vet/treatments/close-month", { month: closeMonth });
      toast.success(`${monthLabel(closeMonth)} kapatıldı · ${r.data.locked_count} kilitlendi · ${r.data.rolled_count} devretti`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kapatılamadı"); }
  };

  const medById = (id) => meds.find(m => m.id === id);
  const addPkgRow = () => {
    const first = meds[0];
    setPkgItems(p => [...p, { _uid: tuid(), medicine_id: first?.id || "", amount: 1, amount_unit: DOSE_UNITS_FOR(first?.usage_unit)[0] || "adet", days: form.treatment_duration_days || "" }]);
  };
  const updatePkgRow = (i, patch) => setPkgItems(p => p.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removePkgRow = (i) => setPkgItems(p => p.filter((_, idx) => idx !== i));
  // Varsayılan Süre (Gün) değişince: elle değiştirilmemiş ilaç satırlarının Gün/Kez'ini senkronla
  const setDuration = (v) => {
    setForm(f => ({ ...f, treatment_duration_days: v }));
    setPkgItems(p => p.map(it => it._daysManual ? it : { ...it, days: v }));
  };
  const applyProtocol = (pid) => {
    setSelectedProtocolId(pid);
    const p = protocols.find(x => x.id === pid);
    if (!p) { setPkgItems([]); return; }
    const dd = p.default_duration_days ?? "";
    setPkgItems((p.items || []).map(it => ({ _uid: tuid(), medicine_id: it.medicine_id, amount: it.amount, amount_unit: it.amount_unit || DOSE_UNITS_FOR(it.stock_unit)[0], days: it.days ?? it.duration_days ?? dd })));
    setForm(f => ({
      ...f,
      diagnosis: f.diagnosis || p.disease || p.name,
      treatment_duration_days: f.treatment_duration_days || (p.default_duration_days ?? ""),
    }));
  };

  // Paket önizleme: doz HAYVAN BAŞINA → GÜNLÜK düşüm = adet × doz; toplam kür = günlük × Gün/Kez.
  const animalCount = Math.max(1, Number(form.animal_count || 1));
  const pkgPreview = pkgItems.map(it => {
    const m = medById(it.medicine_id);
    const perAnimal = m ? convertAmt(Number(it.amount || 0), it.amount_unit, m.usage_unit) : null;
    const deducted = perAnimal != null ? perAnimal * animalCount : null;   // günlük düşüm (stok biriminde)
    const days = Math.max(1, Number(it.days || form.treatment_duration_days || 1));
    const courseTotal = deducted != null ? deducted * days : null;         // toplam kür
    const over = m && deducted != null && deducted > Number(m.stock || 0);
    const cost = m && deducted != null ? deducted * Number(m.unit_price || 0) : 0;  // günlük maliyet
    const db = doseInBase(it.amount, it.amount_unit);
    const highDose = !!(db && db.value > HIGH_DOSE);
    return { it, m, perAnimal, deducted, days, courseTotal, over, cost, doseBase: db, highDose };
  });
  const pkgTotalCost = pkgPreview.reduce((a, r) => a + (r.cost || 0), 0);
  const pkgHasError = pkgPreview.some(r => !r.m || r.deducted == null || r.over);

  const applyToday = async (t) => {
    const applied = (t.applications_count ?? 0) + 1;
    const dur = t.treatment_duration_days || "?";
    const what = t.is_package ? `${t.medicines_count} ilaçlık tedavi paketi` : `${t.medicine_amount} ${t.medicine_unit} ${t.medicine_name}`;
    if (!window.confirm(`${t.diagnosis} için bugünkü uygulamayı kaydet? ${what} stoktan düşülecek (${applied}/${dur} gün).`)) return;
    try {
      const res = await api.post(`/vet/treatments/${t.id}/apply`);
      const costPart = canSeeCosts ? ` (${res.data.applied_application.cost} ₺)` : "";
      toast.success(`${applied}. gün uygulaması kaydedildi${costPart}`);
      load(); loadMeds();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const undoApp = async (tid, appId) => {
    if (!window.confirm("Bu günkü uygulama geri alınsın mı? İlaç stoğa iade edilir.")) return;
    try {
      await api.delete(`/vet/treatments/${tid}/applications/${appId}`);
      toast.success("Geri alındı, stok iade edildi");
      load(); loadMeds();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const selectedMed = meds.find(m => m.id === form.medicine_id);
  const amountNum = Number(form.medicine_amount || 0);
  const singleDoseUnit = form.medicine_amount_unit || selectedMed?.usage_unit;
  // Doz HAYVAN BAŞINA → toplam düşüm = adet × doz
  const singlePerAnimal = selectedMed ? convertAmt(amountNum, singleDoseUnit, selectedMed.usage_unit) : null;
  const singleDeducted = singlePerAnimal != null ? singlePerAnimal * animalCount : null;
  const previewCost = selectedMed && singleDeducted != null ? singleDeducted * Number(selectedMed.unit_price || 0) : 0;
  const overStock = selectedMed && singleDeducted != null && singleDeducted > Number(selectedMed.stock || 0);
  const singleDoseBase = doseInBase(amountNum, singleDoseUnit);
  const singleHighDose = !!(singleDoseBase && singleDoseBase.value > HIGH_DOSE);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.diagnosis) return toast.error("Teşhis zorunludur");
    if (mode === "package") {
      if (!pkgItems.length) return toast.error("Pakete en az bir ilaç ekleyin");
      if (pkgHasError) return toast.error("Pakette hatalı/yetersiz stoklu ilaç var");
      const highItems = pkgPreview.filter(r => r.highDose);
      if (highItems.length) {
        const list = highItems.map(r => `• ${r.m?.name}: ~${r.doseBase.value.toLocaleString("tr-TR")} ${r.doseBase.base}/baş`).join("\n");
        if (!window.confirm(`⚠️ Emin misiniz? Doz çok yüksek!\n\nAşağıdaki ilaçlarda hayvan başına doz ${HIGH_DOSE} birimi aşıyor:\n${list}\n\nYine de kaydedilsin mi?`)) return;
      }
      const body = {
        animal_type: form.animal_type, animal_count: Number(form.animal_count || 1),
        date: form.date, diagnosis: form.diagnosis, dose: form.dose,
        vet_name: form.vet_name?.trim() || user?.name || "",
        treatment_duration_days: form.treatment_duration_days ? Number(form.treatment_duration_days) : null,
        outcome: form.outcome, notes: form.notes,
        protocol_id: selectedProtocolId || null,
        protocol_name: protocols.find(p => p.id === selectedProtocolId)?.name || null,
        medicines: pkgItems.map(it => ({ medicine_id: it.medicine_id, amount: Number(it.amount), amount_unit: it.amount_unit, days: it.days ? Number(it.days) : null })),
      };
      try {
        await api.post("/vet/treatments", body);
        toast.success(canSeeCosts && pkgTotalCost > 0 ? `Tedavi paketi kaydedildi · Maliyet: ${pkgTotalCost.toLocaleString("tr-TR")} ₺` : "Tedavi paketi kaydedildi");
        setForm({ ...form, diagnosis: "", dose: "", notes: "", treatment_duration_days: "" });
        setPkgItems([]); setSelectedProtocolId("");
        load(); loadMeds();
      } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
      return;
    }
    if (overStock) return toast.error("Yetersiz ilaç stoğu");
    if (singleHighDose) {
      if (!window.confirm(`⚠️ Emin misiniz? Doz çok yüksek!\n\nHayvan başına ~${singleDoseBase.value.toLocaleString("tr-TR")} ${singleDoseBase.base} girdiniz (eşik ${HIGH_DOSE} ${singleDoseBase.base}). Yine de kaydedilsin mi?`)) return;
    }
    const body = {
      ...form,
      vet_name: form.vet_name?.trim() || user?.name || "",
      animal_count: Number(form.animal_count || 1),
      medicine_amount: amountNum,
      medicine_amount_unit: form.medicine_amount_unit || null,
      treatment_duration_days: form.treatment_duration_days ? Number(form.treatment_duration_days) : null,
    };
    try {
      await api.post("/vet/treatments", body);
      toast.success(canSeeCosts && previewCost > 0 ? `Tedavi kaydedildi · Maliyet: ${previewCost.toLocaleString("tr-TR")} ₺` : "Tedavi kaydedildi");
      setForm({ ...form, diagnosis: "", dose: "", notes: "", medicine_id: "", medicine_amount: "", medicine_amount_unit: "", treatment_duration_days: "" });
      load(); loadMeds();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const updateOutcome = async (id, outcome) => {
    try {
      await api.patch(`/vet/treatments/${id}`, { outcome });
      toast.success(`Tedavi durumu: ${outcome}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const removeTreatment = async (t) => {
    const apps = t.applications_count ?? 0;
    const costPart = canSeeCosts ? `, ${Number(t.total_cost || 0).toLocaleString("tr-TR")} ₺ finans gideri de kaldırılacak` : "";
    const msg = apps > 0
      ? `"${t.diagnosis}" tedavisi silinsin mi? ${apps} uygulamanın ilaç stoğu iade edilecek${costPart}.`
      : `"${t.diagnosis}" tedavisi silinsin mi?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/vet/treatments/${t.id}`);
      toast.success("Tedavi silindi");
      load(); loadMeds();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const infoTip = (
    <div className="rounded-md border border-[#EEECE5] bg-[#FAFAF7] px-3 py-2.5 text-xs text-[#5e675e]" data-testid="treat-info-tip">
      💡 Tedavi süresi girildiğinde, her gün "Bugün Uygulandı" butonuna basın — sistem her uygulamada ilacı stoktan düşer ve N/M gün ilerlemesini gösterir.
    </div>
  );
  const dayMeta = (entries) => `${entries.reduce((a, x) => a + Number(x.animal_count || 1), 0)} baş`;

  const renderExpanded = (x) => expandedTreatment === x.id ? (
    <>
      {x.is_package && (
        <div className="mb-3">
          <div className="text-xs font-semibold mb-1">💊 Tedavi Paketi İçeriği ({(x.medicines || []).length} ilaç){x.protocol_name ? ` — ${x.protocol_name}` : ""}</div>
          <table className="w-full text-xs" style={{ background: "white", borderRadius: 4 }} data-testid={`treat-pkg-detail-${x.id}`}>
            <thead><tr><th className="text-left px-3 py-2">İlaç</th><th className="text-right">Doz/baş</th><th className="text-right">Günlük Düşüm</th><th className="text-center px-2">İlerleme</th>{canSeeCosts && <th className="text-right px-3">Günlük Maliyet</th>}</tr></thead>
            <tbody>
              {(x.medicines || []).map((m, i) => {
                const md = m.days ?? x.treatment_duration_days ?? 1;
                const ac = m.applied_count ?? 0;
                const done = m.med_completed ?? (ac >= md);
                return (
                  <tr key={`${m.medicine_id}-${i}`} className="border-t border-[#EEECE5]">
                    <td className="px-3 py-1.5">{m.medicine_name}</td>
                    <td className="text-right tabnum">{m.amount} {m.amount_unit}</td>
                    <td className="text-right tabnum">{m.deducted_amount} {m.stock_unit}</td>
                    <td className="text-center px-2 tabnum" data-testid={`treat-pkg-progress-${x.id}-${i}`}>
                      <span className={done ? "text-[#2D4A36] font-semibold" : "text-[#8B6914]"}>{ac}/{md}{done ? " ✓" : ""}</span>
                    </td>
                    {canSeeCosts && <td className="text-right tabnum px-3">{Number(m.cost || 0).toLocaleString("tr-TR")} ₺</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs font-semibold mb-2">📅 {x.diagnosis} — Uygulama Geçmişi ({(x.applications || []).length} kayıt){x.is_locked && <span className="ml-2 text-[#8a938c] font-normal">🔒 kilitli</span>}</div>
      <table className="w-full text-xs" style={{ background: "white", borderRadius: 4 }}>
        <thead><tr><th className="text-left px-3 py-2">Gün</th><th className="text-left">Tarih</th><th className="text-right">Miktar</th>{canSeeCosts && <th className="text-right">Maliyet</th>}<th></th></tr></thead>
        <tbody>
          {(x.applications || []).map((a, i) => (
            <tr key={a.id} className="border-t border-[#EEECE5]">
              <td className="px-3 py-1.5 tabnum">{i + 1}. gün</td>
              <td className="tabnum">{a.date}{a.legacy && <span className="ml-1 text-[10px] text-[#8a938c]">(eski kayıt)</span>}</td>
              <td className="text-right tabnum">{a.items ? `${a.items.length} ilaç` : `${a.amount} ${x.medicine_unit || ""}`}</td>
              {canSeeCosts && <td className="text-right tabnum">{Number(a.cost || 0).toLocaleString("tr-TR")} ₺</td>}
              <td className="text-right px-2">
                {!a.legacy && !x.is_locked && (
                  <button onClick={() => undoApp(x.id, a.id)} data-testid={`treat-undo-${a.id}`} className="text-[#C45B45] hover:underline text-xs">geri al</button>
                )}
              </td>
            </tr>
          ))}
          {!(x.applications || []).length && <tr><td colSpan={canSeeCosts ? 5 : 4} className="text-center py-3 text-[#8a938c]">Henüz uygulama yok</td></tr>}
        </tbody>
      </table>
    </>
  ) : null;

  const sq = search.trim().toLocaleLowerCase("tr");
  const filteredItems = sq
    ? items.filter(x => {
        const hay = [x.diagnosis, x.notes, x.animal_type, x.vet_name, x.medicine_name, x.protocol_name,
          ...(x.is_package ? (x.medicines || []).map(m => m.medicine_name) : [])].filter(Boolean);
        return hay.some(s => String(s).toLocaleLowerCase("tr").includes(sq));
      })
    : items;

  const treatColumns = [
    { header: "Hayvan", cell: x => <span className="capitalize font-semibold">{x.animal_type || x.animal_category || "inek"}</span> },
    { header: "Adet", thClass: "text-right", tdClass: "text-right tabnum", cell: x => x.animal_count || 1 },
    { header: "Teşhis", cell: x => x.diagnosis },
    { header: "İlaç / Paket", cell: x => x.is_package
      ? <span className="font-medium text-[#2D4A36]"><PackageIcon size={12} className="inline mr-1" weight="duotone" />Tedavi Paketi ({x.medicines_count} ilaç)</span>
      : (x.medicine_name ? `${x.medicine_name}${x.medicine_amount ? ` (${x.medicine_amount} ${x.medicine_unit || ""}/baş·gün)` : ""}` : "—") },
    { header: "İlerleme", tdClass: "min-w-[110px]", cell: x => {
      const applied = x.applications_count ?? 0;
      const dur = x.treatment_duration_days || 0;
      const pct = dur > 0 ? Math.min(100, (applied / dur) * 100) : 0;
      return dur > 0 ? (
        <div>
          <div className="flex justify-between text-[11px] mb-0.5 tabnum">
            <span className="font-semibold">{applied}/{dur} gün</span>
            {x.completed && <span className="text-[#2D4A36]">✓</span>}
          </div>
          <div className="h-1.5 bg-[#F5F2EC] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: x.completed ? "#2D4A36" : x.applied_today ? "#8B6914" : "#C45B45" }} />
          </div>
          {x.applied_today && !x.completed && <div className="text-[10px] text-[#8B6914] mt-0.5">✓ bugün yapıldı</div>}
        </div>
      ) : <span className="text-xs text-[#8a938c]">süresiz</span>;
    } },
    { header: "Durum", cell: x => x.is_locked ? (
      <span className="inline-flex items-center gap-1 text-xs font-semibold" data-testid={`treat-locked-${x.id}`}>
        <Lock size={12} weight="fill" className="text-[#8a938c]" />
        <span className={OUTCOME_TONE[x.outcome] || ""}>{x.outcome}</span>
      </span>
    ) : (
      <Select value={x.outcome} onChange={e => updateOutcome(x.id, e.target.value)} className="!py-1 !px-2 text-xs !w-auto" data-testid={`treat-outcome-${x.id}`}>
        <option>tedavi görüyor</option><option>iyileşti</option><option>vefat</option><option>iptal</option>
      </Select>
    ) },
    ...(canSeeCosts ? [{ header: "Toplam Maliyet", thClass: "text-right", tdClass: "text-right tabnum font-semibold", cell: x => `${Number(x.total_cost ?? x.cost ?? 0).toLocaleString("tr-TR")} ₺` }] : []),
    { header: "", thClass: "text-right", tdClass: "text-right", cell: x => {
      const historyBtn = (
        <button onClick={() => setExpandedTreatment(expandedTreatment === x.id ? null : x.id)} data-testid={`treat-history-${x.id}`}
          className="p-1.5 rounded hover:bg-[#F5F2EC] text-xs" title="Uygulama geçmişi">{(x.applications || []).length}</button>
      );
      if (x.is_locked) return <div className="flex gap-1 justify-end items-center">{historyBtn}<Lock size={13} weight="fill" className="text-[#b3b0a6]" /></div>;
      const canApply = (x.is_package ? (x.medicines_count > 0) : (x.medicine_id && Number(x.medicine_amount || 0) > 0)) && !x.applied_today && !x.completed && x.outcome === "tedavi görüyor";
      return (
        <div className="flex gap-1 justify-end">
          {canApply && (
            <button onClick={() => applyToday(x)} data-testid={`treat-apply-${x.id}`}
              className="p-1.5 rounded bg-[#2D4A36] text-white hover:bg-[#3d5f45] text-xs font-semibold px-2" title="Bugünkü uygulamayı kaydet">Bugün ✓</button>
          )}
          {historyBtn}
          <button onClick={() => removeTreatment(x)} data-testid={`treat-delete-${x.id}`}
            className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45] text-xs" title="Tedaviyi sil">×</button>
        </div>
      );
    } },
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">Tedavi Kaydı</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hayvan Tipi *">
              <Select data-testid="treat-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Adet *">
              <Input data-testid="treat-count" type="number" min="1" required value={form.animal_count} onChange={e => setForm({ ...form, animal_count: e.target.value })} />
            </Field>
          </div>
          <Field label="Tarih *"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Teşhis *"><Input data-testid="treat-diag" value={form.diagnosis} onChange={e => setForm({ ...form, diagnosis: e.target.value })} placeholder="Mastitis, Solunum enf..." /></Field>
          {/* Mod seçici: Tek İlaç / Tedavi Paketi */}
          <div className="flex gap-2" data-testid="treat-mode-toggle">
            <button type="button" data-testid="treat-mode-single" onClick={() => setMode("single")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${mode === "single" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#3d4740] border-[#E5E2DC] hover:bg-[#F5F2EC]"}`}>Tek İlaç</button>
            <button type="button" data-testid="treat-mode-package" onClick={() => setMode("package")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${mode === "package" ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#3d4740] border-[#E5E2DC] hover:bg-[#F5F2EC]"}`}><PackageIcon size={14} className="inline mr-1" />Tedavi Paketi</button>
          </div>

          {mode === "single" ? (
            <>
              <Field label="İlaç">
                <Select data-testid="treat-med" value={form.medicine_id}
                        onChange={e => { const m = medById(e.target.value); setForm({ ...form, medicine_id: e.target.value, medicine_amount_unit: m ? DOSE_UNITS_FOR(m.usage_unit)[0] : "" }); }}>
                  <option value="">— İlaç seçin (opsiyonel) —</option>
                  {meds.map(m => <option key={m.id} value={m.id}>{`${m.name} (stok: ${m.stock} ${m.usage_unit || ""})`}</option>)}
                </Select>
                {!meds.length && <div className="text-xs text-[#C45B45] mt-1">Önce "İlaçlar" menüsünden ilaç ekleyin.</div>}
              </Field>
              {selectedMed && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Günlük Doz (Hayvan Başına) *">
                    <Input data-testid="treat-amount" type="number" step="0.0001" min="0" required value={form.medicine_amount} onChange={e => setForm({ ...form, medicine_amount: e.target.value })} placeholder="Örn: 20" />
                  </Field>
                  <Field label="Birim">
                    <Select data-testid="treat-amount-unit" value={singleDoseUnit} onChange={e => setForm({ ...form, medicine_amount_unit: e.target.value })}>
                      {DOSE_UNITS_FOR(selectedMed.usage_unit).map(u => <option key={u} value={u}>{u}</option>)}
                    </Select>
                  </Field>
                  <div className="col-span-2 text-[10px] text-[#8a938c] -mt-1">Doz <b>hayvan başına</b> girilir; stoktan <b>adet × doz</b> düşülür. Stok birimi: <b>{selectedMed.usage_unit}</b>. Farklı birim otomatik çevrilir (1 L = 1000 ml). 1. gün otomatik uygulanır.</div>
                </div>
              )}
              {selectedMed && amountNum > 0 && (
                <div className={`text-xs px-3 py-2 rounded-md border ${overStock ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8a3a2b]" : singleHighDose ? "bg-[#FDF3E3] border-[#EBD9B4] text-[#8B6914]" : "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="treat-preview">
                  {overStock ? (
                    <>⚠️ Yetersiz stok: mevcut {selectedMed.stock} {selectedMed.usage_unit}, gereken {singleDeducted?.toLocaleString("tr-TR")} {selectedMed.usage_unit} ({animalCount} baş × {singlePerAnimal?.toLocaleString("tr-TR")})</>
                  ) : (
                    <>
                      {singleHighDose && <div className="font-semibold mb-1" data-testid="treat-highdose-warning">⚠️ Doz çok yüksek! Hayvan başına ~{singleDoseBase.value.toLocaleString("tr-TR")} {singleDoseBase.base} (eşik {HIGH_DOSE} {singleDoseBase.base}). Kaydederken onay istenecek.</div>}
                      {canSeeCosts ? (
                        <>✓ {animalCount} baş × {amountNum} {singleDoseUnit} = <b>{singleDeducted?.toLocaleString("tr-TR")} {selectedMed.usage_unit}</b> × {selectedMed.unit_price} ₺ = <b>{previewCost.toLocaleString("tr-TR")} ₺</b><br />Stok sonrası: {(Number(selectedMed.stock) - (singleDeducted || 0)).toLocaleString("tr-TR")} {selectedMed.usage_unit}</>
                      ) : (
                        <>✓ {animalCount} baş × {amountNum} {singleDoseUnit} = <b>{singleDeducted?.toLocaleString("tr-TR")} {selectedMed.usage_unit}</b> düşülecek<br />Stok sonrası: {(Number(selectedMed.stock) - (singleDeducted || 0)).toLocaleString("tr-TR")} {selectedMed.usage_unit}</>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="Protokolden Doldur (opsiyonel)">
                <Select data-testid="treat-protocol-select" value={selectedProtocolId} onChange={e => applyProtocol(e.target.value)}>
                  <option value="">— Protokol seçin —</option>
                  {protocols.map(p => <option key={p.id} value={p.id}>{p.name}{p.disease ? ` (${p.disease})` : ""}</option>)}
                </Select>
                {!protocols.length && <div className="text-xs text-[#8a938c] mt-1">İlaçlar → Tedavi Protokolleri'nden paket oluşturabilirsiniz.</div>}
              </Field>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-semibold text-[#2D4A36]">Paketteki İlaçlar</label>
                  <Button type="button" variant="secondary" data-testid="treat-pkg-add" onClick={addPkgRow} disabled={!meds.length}><Plus size={14} className="inline mr-1" />İlaç Ekle</Button>
                </div>
                <div className="space-y-2">
                  {pkgItems.map((it, i) => {
                    const m = medById(it.medicine_id);
                    const r = pkgPreview[i];
                    return (
                      <div key={it._uid} data-testid={`treat-pkg-row-${i}`} className={`rounded-lg p-2 border ${r?.over ? "bg-[#fbeee9] border-[#f2d4cb]" : "bg-[#F5F2EC] border-[#E5E2DC]"}`}>
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-4"><Field label="İlaç"><Select data-testid={`treat-pkg-med-${i}`} value={it.medicine_id} onChange={e => { const mm = medById(e.target.value); updatePkgRow(i, { medicine_id: e.target.value, amount_unit: DOSE_UNITS_FOR(mm?.usage_unit)[0] }); }}>{meds.map(m2 => <option key={m2.id} value={m2.id}>{m2.name} (stok: {m2.stock} {m2.usage_unit})</option>)}</Select></Field></div>
                          <div className="col-span-2"><Field label="Doz/baş"><Input data-testid={`treat-pkg-amount-${i}`} type="number" step="0.0001" min="0" value={it.amount} onChange={e => updatePkgRow(i, { amount: e.target.value })} /></Field></div>
                          <div className="col-span-2"><Field label="Birim"><Select data-testid={`treat-pkg-unit-${i}`} value={it.amount_unit} onChange={e => updatePkgRow(i, { amount_unit: e.target.value })}>{DOSE_UNITS_FOR(m?.usage_unit).map(u => <option key={u} value={u}>{u}</option>)}</Select></Field></div>
                          <div className="col-span-3"><Field label="Gün/Kez"><Input data-testid={`treat-pkg-days-${i}`} type="number" min="1" step="1" value={it.days} onChange={e => updatePkgRow(i, { days: e.target.value, _daysManual: true })} placeholder={form.treatment_duration_days || "1"} /></Field></div>
                          <div className="col-span-1 flex justify-center pb-1"><button type="button" data-testid={`treat-pkg-del-${i}`} onClick={() => removePkgRow(i)} className="p-2 rounded-md text-[#C45B45] hover:bg-[#FBECEA]"><Trash size={16} weight="bold" /></button></div>
                        </div>
                        {r?.m && r.deducted != null && (
                          <div className="text-[10px] mt-1 px-1 text-[#5e675e]">
                            {r.over ? <span className="text-[#C45B45]">⚠️ Yetersiz: mevcut {r.m.stock} {r.m.usage_unit}, günlük gereken {r.deducted.toLocaleString("tr-TR")} ({animalCount} baş × {r.perAnimal?.toLocaleString("tr-TR")})</span>
                              : <>{r.highDose && <span className="text-[#8B6914] font-semibold" data-testid={`treat-pkg-highdose-${i}`}>⚠️ Doz yüksek (~{r.doseBase.value.toLocaleString("tr-TR")} {r.doseBase.base}/baş) · </span>}{animalCount} baş × {r.it.amount} {r.it.amount_unit} × {r.days} gün = <b>{r.courseTotal?.toLocaleString("tr-TR")} {r.m.usage_unit}</b> düşülecek · günlük {r.deducted?.toLocaleString("tr-TR")} {r.m.usage_unit}{canSeeCosts ? ` · ${r.cost.toLocaleString("tr-TR")} ₺/gün` : ""}</>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!pkgItems.length && <div className="text-sm text-[#8a938c] py-2">Protokol seçin veya "İlaç Ekle" ile ilaç ekleyin.</div>}
                </div>
                {pkgItems.length > 0 && canSeeCosts && (
                  <div className="text-xs mt-2 px-3 py-2 rounded-md border bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]" data-testid="treat-pkg-preview">
                    Bugün (1. gün) düşülecek maliyet: <b>{pkgTotalCost.toLocaleString("tr-TR")} ₺</b> ({pkgItems.length} ilaç) · sonraki günler "Bugün ✓" ile
                  </div>
                )}
              </div>
            </>
          )}

          <Field label="Doz Talimatı"><Input value={form.dose} onChange={e => setForm({ ...form, dose: e.target.value })} placeholder="Örn: 2x/gün 3 gün" /></Field>
          <Field label={mode === "package" ? "Varsayılan Süre (Gün)" : "Tedavi Süresi (gün)"}>
            <Input data-testid="treat-duration" type="number" min="0" value={form.treatment_duration_days} onChange={e => setDuration(e.target.value)} placeholder="5" />
            <div className="text-[10px] text-[#8a938c] mt-1">{mode === "package" ? "Her ilacın \"Gün/Kez\" alanına varsayılan olarak yazılır; ilaç bazında ayrı değiştirebilirsiniz." : "Tabloda \"N/M gün\" olarak gösterilir. Her gün \"Bugün ✓\" butonuna bas."}</div>
          </Field>
          <PerformerField label="Veteriner (İşlemi Yapan)" user={user} value={form.vet_name}
            onChange={v => setForm({ ...form, vet_name: v })} testId="treat-vet" />
          <Field label="Sonuç">
            <Select value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })}>
              <option>tedavi görüyor</option><option>iyileşti</option><option>vefat</option>
            </Select>
          </Field>
          <Field label="Notlar"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>

          <Button data-testid="treat-submit" type="submit" disabled={mode === "single" ? overStock : pkgHasError}><Plus size={14} className="inline mr-1" /> Kaydet</Button>
        </form>
      </Card>
      <div className="lg:col-span-2 space-y-3">
        {/* Durum sekmeleri + rozetler */}
        <div className="flex flex-wrap gap-1.5" data-testid="treat-tabs">
          {TREAT_TABS.map(tb => {
            const active = tab === tb.key;
            const badge = tb.countKey ? (counts[tb.countKey] ?? 0) : null;
            return (
              <button key={tb.key} data-testid={`treat-tab-${tb.key}`} onClick={() => setTab(tb.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 transition-colors ${active ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
                {tb.key === "archive" && <Archive size={14} weight="bold" />}{tb.label}
                {badge != null && <span className={`tabnum text-[11px] px-1.5 rounded-full ${active ? "bg-white/25" : "bg-[#F0EEE8] text-[#8a938c]"}`} data-testid={`treat-tab-count-${tb.key}`}>{badge}</span>}
              </button>
            );
          })}
        </div>

        {/* Arama + Aylık Kapanış (yönetici) */}
        {tab !== "archive" && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a938c]" />
              <Input data-testid="treat-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Teşhis veya hayvan ara…" className="!pl-8" />
            </div>
            {isManager && (
              <div className="flex items-center gap-1.5">
                <Input type="month" data-testid="treat-close-month" value={closeMonth} onChange={e => setCloseMonth(e.target.value)} className="!w-auto" />
                <Button data-testid="treat-close-btn" onClick={doCloseMonth} className="!bg-[#8B2E1D] hover:!bg-[#71251a] whitespace-nowrap">
                  <Lock size={13} weight="bold" className="inline mr-1" />Ayı Kapat
                </Button>
              </div>
            )}
          </div>
        )}

        {/* NORMAL GÖRÜNÜM — Aylık Kartlar */}
        {tab !== "archive" && (
          <>
            {infoTip}
            <TreatmentMonthlyCards items={filteredItems} statusTab={tab}
              columns={treatColumns} renderExpanded={renderExpanded} dayMeta={dayMeta}
              emptyText={search.trim() ? "Aramayla eşleşen tedavi yok" : "Bu durumda tedavi kaydı yok"} />
          </>
        )}

        {/* ARŞİV GÖRÜNÜMÜ — kilitli geçmiş aylar */}
        {tab === "archive" && (
          <div className="space-y-2" data-testid="treat-archive">
            {!closures.length && <Card className="text-center text-[#8a938c] py-8">Henüz kapatılmış ay yok. Bir ayı kapattığınızda burada arşivlenir.</Card>}
            {closures.map(c => (
              <Card key={c.id} className="!p-0 overflow-hidden" testId={`treat-archive-month-${c.month}`}>
                <button onClick={() => openArchiveMonth(c.month)} data-testid={`treat-archive-toggle-${c.month}`}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F7F6F3] text-left">
                  <span className="inline-flex items-center gap-2 font-semibold text-[#2D4A36]">
                    <Lock size={14} weight="fill" className="text-[#8a938c]" />{monthLabel(c.month)}
                  </span>
                  <span className="text-xs text-[#8a938c]">{c.locked_count ?? 0} kilitli kayıt{c.closed_at ? ` · kapandı ${(c.closed_at || "").slice(0, 10)}` : ""}</span>
                </button>
                {archiveOpen === c.month && (
                  <div className="border-t border-[#ECE8E0]">
                    <GroupedTable testId={`treat-arch-${c.month}`} items={archiveItems} pageSize={1000}
                      emptyText="Bu ayda kilitli kayıt yok" dayMeta={dayMeta} renderExpanded={renderExpanded} columns={treatColumns} />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


